/**
 * @file DuplicateBillsReview.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-12
 * @modified 2026-10-12
 * @description Possible duplicate bills, for everyone to review (Cam,
 *              2026-10-12): pairs saved anyway from Bulk Add Bills ("Continue
 *              as normal") and pairs the nightly duplicate check found
 *              (billService.flagDuplicateBills) — one list,
 *              purchasing.bill_duplicate_flags via /api/bulk-bills/duplicate-flags.
 *
 *              DuplicateBillsNotice is the strip Search Bills shows every user,
 *              every time it loads, while any are open; "Review" opens
 *              DuplicateBillsReview: both bills side by side (supplier,
 *              numbers, date, total, status, in Xero or not), each opening in
 *              the bill window to void or archive it with the actions there,
 *              and "Not a duplicate" / "Dealt with" to resolve the pair (who
 *              and when are kept, with an optional note). A pair where one bill
 *              is voided or deleted resolves itself.
 *
 * 1.1.0 (review): the notice asks only for the count
 *              (/duplicate-flags/count); pairs with a voided bill are left out
 *              of both and resolved by the duplicate check, not by reading.
 */

import React, { useCallback, useEffect, useState } from 'react';
import Modal from './Modal';
import Input from './Input';
import EntityLink from './EntityLink';
import LoadingSpinner from './LoadingSpinner';
import { useEntityModal } from './EntityModalProvider';
import { useSlideOutButton } from './SlideOutButton';
import { API_URL } from '../config';
import { formatPrice } from '../utils/currencyFormatter';
import { formatDate, formatDateTime } from '../utils/dateFormatters';
import { billStatusLabel, billInXero } from '../utils/billStatus';
import './BulkAddBills.css';

const SOURCE = { bulk_add: 'Saved anyway in Bulk Add Bills', nightly: 'Found by the nightly duplicate check' };
const REASON = { invoice_number: 'same supplier and invoice number', total_and_date: 'same supplier, total and date' };
const RESOLUTION = { not_duplicate: 'Not a duplicate', dealt_with: 'Dealt with', one_voided: 'One of them was voided or deleted' };

/** GET the review list; { ready, flags, open_count } or null. */
export async function fetchDuplicateFlags(status = 'open') {
  try {
    const res = await fetch(`${API_URL}/api/bulk-bills/duplicate-flags${status === 'resolved' ? '?status=resolved' : ''}`, { credentials: 'include' });
    const d = await res.json();
    return d.success ? d : null;
  } catch {
    return null;
  }
}

function BillCard({ bill, onOpen }) {
  if (!bill) return null;
  return (
    <div className="bulk-bills-dup-card">
      <h4>
        <EntityLink type="bill" id={bill.id}>{bill.bill_number || bill.supplier_reference || `Bill #${bill.id}`}</EntityLink>
      </h4>
      <dl>
        <dt>Supplier</dt><dd>{bill.supplier_name || '—'}</dd>
        <dt>Invoice no.</dt><dd>{bill.supplier_reference || '—'}</dd>
        <dt>Date</dt><dd>{bill.bill_date ? formatDate(bill.bill_date) : '—'}</dd>
        <dt>Total</dt><dd>{bill.total != null ? formatPrice(bill.total) : '—'}</dd>
        <dt>Status</dt>
        <dd>
          {billStatusLabel(bill.status)}{bill.is_queried ? ' · Queried' : ''}{bill.is_archived ? ' · Archived' : ''}
          {' · '}{billInXero(bill) ? 'in Xero' : (bill.source === 'local' ? 'not in Xero yet' : 'from Xero')}
        </dd>
        <dt>Entered</dt><dd>{bill.source === 'local' ? 'here' : 'in Xero'}{bill.created_at ? `, ${formatDateTime(bill.created_at)}` : ''}</dd>
        <dt>PDF</dt><dd>{bill.attachment_filename || 'none'}</dd>
      </dl>
      <button type="button" className="btn-slideout" onClick={() => onOpen(bill.id)}
        title="Open the bill — void or archive it there if it is the copy to go">
        <div className="icon-wrapper"><span className="material-symbols-rounded">open_in_new</span></div>
        <span className="btn-label">Open this bill</span>
      </button>
    </div>
  );
}

function ReviewBody({ flags, resolved, loading, notes, setNotes, onResolve, busyId, onOpen }) {
  useSlideOutButton([flags.length, resolved, busyId]);
  if (loading) return <LoadingSpinner />;
  if (!flags.length) {
    return <p className="muted">{resolved ? 'Nothing resolved yet.' : 'No possible duplicates to review.'}</p>;
  }
  return (
    <div className="bulk-bills-dup-list">
      {flags.map(f => (
        <div className="bulk-bills-dup-pair" key={f.id}>
          <div className="bulk-bills-dup-pair-head">
            <strong>{REASON[f.reason] || 'Possible duplicate'}</strong>
            <span className="muted">
              {' · '}{SOURCE[f.source] || f.source}{f.created_by_name ? ` by ${f.created_by_name}` : ''}, {formatDateTime(f.created_at)}
            </span>
            {resolved && (
              <div className="muted">
                {RESOLUTION[f.resolution] || f.resolution}{f.resolved_by_name ? ` — ${f.resolved_by_name}` : ''}{f.resolved_at ? `, ${formatDateTime(f.resolved_at)}` : ''}
                {f.resolution_note ? ` · "${f.resolution_note}"` : ''}
              </div>
            )}
          </div>
          <div className="bulk-bills-dup-grid two">
            <BillCard bill={f.other} onOpen={onOpen} />
            <BillCard bill={f.bill} onOpen={onOpen} />
          </div>
          {!resolved && (
            <div className="bulk-bills-dup-resolve">
              <Input
                type="text"
                icon="edit_note"
                maxLength={500}
                placeholder="Note (optional) — e.g. two deliveries on the same day"
                value={notes[f.id] || ''}
                onChange={(e) => setNotes(prev => ({ ...prev, [f.id]: e.target.value }))}
                aria-label="Note on this pair"
              />
              <div className="buttons slideout">
                <div><button className="btn-slideout" disabled={busyId === f.id} data-write-action
                  onClick={() => onResolve(f, 'dealt_with')}
                  title="One of them has been voided or archived (or otherwise sorted) — take it off the list">
                  <div className="icon-wrapper"><span className="material-symbols-rounded">task_alt</span></div>
                  <span className="btn-label">Dealt with</span>
                </button></div>
                <div><button className="btn-slideout btn-slideout-primary" disabled={busyId === f.id} data-write-action
                  onClick={() => onResolve(f, 'not_duplicate')}
                  title="Both are real bills — never flag this pair again">
                  <div className="icon-wrapper"><span className="material-symbols-rounded">done</span></div>
                  <span className="btn-label">Not a duplicate</span>
                </button></div>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export function DuplicateBillsReview({ isOpen, onClose, onChanged }) {
  const { openEntity } = useEntityModal();
  const [resolved, setResolved] = useState(false);
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const d = await fetchDuplicateFlags(resolved ? 'resolved' : 'open');
    setFlags(d?.flags || []);
    setLoading(false);
  }, [resolved]);

  useEffect(() => { if (isOpen) load(); }, [isOpen, load]);

  const resolve = async (f, resolution) => {
    setBusyId(f.id);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/bulk-bills/duplicate-flags/${f.id}/resolve`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ resolution, note: notes[f.id] || null }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || !d.success) throw new Error(d.error || 'It could not be resolved');
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  };

  // A bill opened from here may be voided or archived there: look again when it closes.
  const openBill = (id) => openEntity('bill', id, { onClose: () => { load(); if (onChanged) onChanged(); } });

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" className="bulk-bills-dup-review" title="Possible duplicate bills">
      <div className="bulk-bills-dup-tabs">
        <div className="checkbox-container">
          <div className="check light">
            <input id="dup-show-resolved" type="checkbox" checked={resolved} onChange={(e) => setResolved(e.target.checked)} />
            <label htmlFor="dup-show-resolved"></label>
          </div>
        </div>
        <label htmlFor="dup-show-resolved">Show resolved</label>
        <span className="muted">
          Each pair looks like one supplier invoice entered twice. Open a bill to void or archive the copy that should go, or mark the pair as not a duplicate.
        </span>
      </div>
      {error && <div className="bulk-bills-callout error"><span className="material-symbols-rounded">error</span><div>{error}</div></div>}
      <ReviewBody flags={flags} resolved={resolved} loading={loading} notes={notes} setNotes={setNotes}
        onResolve={resolve} busyId={busyId} onOpen={openBill} />
    </Modal>
  );
}

/** The strip on Search Bills while any possible duplicate is open. */
export function DuplicateBillsNotice({ refreshKey = 0, onClosed = null }) {
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const check = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/bulk-bills/duplicate-flags/count`, { credentials: 'include' });
      const d = await res.json();
      setCount(d?.success && d.ready ? (d.open_count || 0) : 0);
    } catch {
      setCount(0);
    }
  }, []);
  useEffect(() => { check(); }, [check, refreshKey]);
  useSlideOutButton([count > 0]);
  if (!count && !open) return null;
  return (
    <>
      {count > 0 && (
        <div className="bulk-bills-callout warn bulk-bills-dup-notice" role="status">
          <span className="material-symbols-rounded">content_copy</span>
          <div>
            <strong>{count} possible duplicate bill{count === 1 ? '' : 's'} to review.</strong>{' '}
            The same supplier invoice may have been entered twice.
          </div>
          <div className="buttons slideout">
            <div><button className="btn-slideout" onClick={() => setOpen(true)}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">rule</span></div>
              <span className="btn-label">Review</span>
            </button></div>
          </div>
        </div>
      )}
      <DuplicateBillsReview isOpen={open} onClose={() => { setOpen(false); check(); if (onClosed) onClosed(); }} onChanged={check} />
    </>
  );
}

export default DuplicateBillsReview;
